import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Buildings, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AcceptCenterInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [pwd, setPwd] = useState({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/auth/center-invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Invito non valido'));
    axios.get(`${API}/api/auth/password-requirements`)
      .then((r) => setRequirements(r.data.requirements || []))
      .catch(() => {});
  }, [token]);

  const accept = async () => {
    if (pwd.password !== pwd.confirm) { toast.error('Le due password non coincidono'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/auth/center-invite/${token}/accept`, { password: pwd.password });
      await login(r.data.token, r.data.user);
      toast.success('Accesso attivato: completa il profilo del centro');
      navigate(r.data.next || '/professional/completa-profilo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore attivazione accesso');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5FAFF] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md rounded-2xl p-8" data-testid="accept-center-invite-page">
        <img src="/vivalink-logo-transparent.png" alt="VivaLink Italia" className="h-14 mx-auto mb-6 object-contain" />

        {error ? (
          <div className="text-center space-y-3" data-testid="center-invite-error">
            <WarningCircle size={40} weight="duotone" className="text-amber-600 mx-auto" />
            <p className="font-heading font-medium text-lg">Invito non utilizzabile</p>
            <p className="text-sm text-foreground/70">{error}</p>
            <Button variant="outline" className="rounded-full" onClick={() => navigate('/login')} data-testid="center-invite-to-login">
              Vai all'accesso
            </Button>
          </div>
        ) : !invite ? (
          <p className="text-center text-foreground/60" data-testid="center-invite-loading">Verifica invito…</p>
        ) : (
          <div className="space-y-5">
            <div className="text-center">
              <Buildings size={36} weight="duotone" className="text-primary mx-auto mb-2" />
              <h1 className="font-heading font-semibold text-2xl" data-testid="center-invite-name">{invite.center_name}</h1>
              <p className="text-sm text-foreground/70 mt-1">
                L'amministratore di VivaLink Italia ha creato la scheda del tuo Centro
                {invite.city ? ` a ${invite.city}` : ''}{invite.province ? ` (${invite.province})` : ''}.
              </p>
              <p className="text-xs text-foreground/55 mt-2">
                Imposta la password per attivare l'accesso: al primo ingresso completerai P.IVA, zone di copertura e tariffario.
              </p>
              {invite.converts_role && (
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 text-left" data-testid="center-invite-conversion">
                  Con questa email hai già un account {invite.converts_role === 'professional' ? 'da professionista' : 'cliente'}:
                  attivando il Centro diventerà l'account del Centro e la nuova password sostituirà quella attuale.
                  {invite.converts_role === 'professional' ? ' Il tuo profilo da professionista indipendente non sarà più visibile.' : ''}
                </p>
              )}
            </div>

            <div>
              <Label>Email di accesso</Label>
              <Input value={invite.email} disabled data-testid="center-invite-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={pwd.password} onChange={(e) => setPwd({ ...pwd, password: e.target.value })} data-testid="center-invite-password" />
            </div>
            <div>
              <Label>Conferma password</Label>
              <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="center-invite-password-confirm" />
            </div>
            {requirements.length > 0 && (
              <ul className="text-xs text-foreground/60 space-y-0.5">
                {requirements.map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            )}
            <Button onClick={accept} disabled={busy || !pwd.password} className="rounded-full w-full" data-testid="center-invite-accept-btn">
              Attiva l'accesso del Centro
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AcceptCenterInvitePage;
